import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, ChevronDown, ChevronUp, FileText, Copy, Check } from 'lucide-react';
import './EmendaDetailsModal.css';

export default function EmendaDetailsModal({ emenda, columns, onClose }) {
  const [expandedFields, setExpandedFields] = useState(new Set());
  const [copiedField, setCopiedField] = useState(null);

  // Detectar campos com textos longos (mais de 150 caracteres)
  const isLongText = (value) => {
    return value && String(value).length > 150;
  };

  // Detectar campos que devem ser sempre expandíveis
  const isExpandableField = (columnHeader) => {
    const expandableKeywords = [
      'objeto', 'motivo', 'justificativa', 'descrição', 'observação',
      'detalhamento', 'fundamentação', 'proposta', 'finalidade'
    ];
    return expandableKeywords.some(keyword => 
      columnHeader.toLowerCase().includes(keyword)
    );
  };

  const toggleExpand = (columnKey) => {
    setExpandedFields(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (text, fieldKey) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const formatValue = (value, columnHeader) => {
    if (!value || value === '-') return '-';
    
    // Detectar se é uma coluna de valor monetário
    const isValueColumn = columnHeader.toLowerCase().includes('valor') || 
                         columnHeader.toLowerCase().includes('vlr');
    
    if (isValueColumn) {
      // Converter valor americano para número
      const numericValue = parseFloat(String(value).replace(/[R$\s,]/g, ''));
      
      if (!isNaN(numericValue)) {
        // Formatar em Real brasileiro
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(numericValue);
      }
    }
    
    return value;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
      >
          <div className="modal-header">
            <h2 className="modal-title">Detalhes da Emenda</h2>
            <button
              onClick={onClose}
              className="modal-close"
              aria-label="Fechar modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="modal-body">
            {columns.map((column, index) => {
              const value = emenda[column.accessorKey];
              const formattedValue = formatValue(value, column.header);
              const isLong = isLongText(value);
              const isExpandable = isExpandableField(column.header);
              const shouldShowExpandButton = isLong || isExpandable;
              const isExpanded = expandedFields.has(column.accessorKey);
              const fieldKey = `${column.accessorKey}-${index}`;
              const isCopied = copiedField === fieldKey;

              return (
                <div 
                  key={index} 
                  className={`modal-field ${
                    shouldShowExpandButton ? 'modal-field--expandable' : ''
                  } ${
                    isExpanded ? 'modal-field--expanded' : ''
                  }`}
                  style={{
                    gridColumn: (isExpanded && isLong) ? '1 / -1' : 'auto'
                  }}
                  onClick={() => {
                    if (shouldShowExpandButton) {
                      toggleExpand(column.accessorKey);
                    }
                  }}
                >
                  <div className="modal-field__header">
                    <span className="modal-field__label">
                      {isExpandable && <FileText className="w-3 h-3" />}
                      {column.header}
                    </span>
                    {shouldShowExpandButton && (
                      <div className="modal-field__actions" onClick={(e) => e.stopPropagation()}>
                        {isLong && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(String(value), fieldKey);
                            }}
                            className="modal-field__action-btn"
                            title="Copiar texto"
                          >
                            {isCopied ? (
                              <Check className="w-3.5 h-3.5 text-green-600" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(column.accessorKey);
                          }}
                          className="modal-field__action-btn"
                          title={isExpanded ? 'Recolher' : 'Expandir'}
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className={`modal-field__content ${
                    isLong ? 'modal-field__content--long' : ''
                  } ${
                    isExpanded ? 'modal-field__content--expanded' : ''
                  }`}>
                    <span className="modal-field__value">
                      {formattedValue}
                    </span>
                    {!isExpanded && isLong && (
                      <div className="modal-field__gradient" />
                    )}
                  </div>

                  {!isExpanded && isLong && (
                    <div className="modal-field__preview-hint">
                      <span>Clique para ver texto completo ({String(value).length} caracteres)</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="modal-footer">
            <button onClick={onClose} className="modal-button">
              Fechar
            </button>
          </div>
        </motion.div>
    </div>
  );
}
